import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit
} from "@angular/core";
import {SessionModel} from "../../orchestration/session.model";
import {Subscription} from "rxjs";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {ButtonConnotation, ButtonLayout, ButtonProminence, ButtonTier} from "./button";
import {SessionEvents} from "../../orchestration/session-events";
import {ButtonShape, ButtonStyle} from "../../orchestration/app-entity.model";

// noinspection GrazieInspection, AiaStyle
@Component({
    selector: "woa-button",
    templateUrl: "./button.component.html",
})
export class ButtonComponent implements OnInit, OnDestroy {
    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    ButtonStyle = ButtonStyle;

    /**
     * Tier, prominence, and connotation classify the set of possible logical buttons.
     * These are the values which are typically specified in the template using the button.
     */
    private _tier: ButtonTier = ButtonTier.FIRST;
    private _prominence: ButtonProminence = ButtonProminence.HIGH;
    private _connotation: ButtonConnotation = ButtonConnotation.AFFIRMATIVE;

    /**
     * Shape, size, style, and color, control the set of possible physical buttons.
     * They are not typically specified directly, but rather derived from the app config based on button tier, prominence, and connotation.
     * Shape is configured per-tier in the app-config.
     * Default size parameters are defined per-tier in default-theme.scss, and configurable in the app-config.
     * Style is configured per-prominence/tier combination in the app-config.
     * Color usage is derived per style/connotation combination in button.component.scss.
     */
    private _shape: ButtonShape;
    private _style: ButtonStyle;

    private shapeSpecified: boolean = false;
    private styleSpecified: boolean = false;
    private subscriptions: Subscription = new Subscription();

    /**
     * Support for custom color overrides.
     */
    @Input() @HostBinding("class.override-flat-background-color") overrideFlatBackgroundColor: boolean = false; // Set this and set --woa-button-flat-background-color
    @Input() @HostBinding("class.override-flat-foreground-color") overrideFlatForegroundColor: boolean = false; // Set this and set --woa-button-flat-foreground-color

    @Input() @HostBinding("class.override-outline-background-color") overrideOutlineBackgroundColor: boolean = false; // Set this and set --woa-button-outline-background-color
    @Input() @HostBinding("class.override-outline-foreground-color") overrideOutlineForegroundColor: boolean = false; // Set this and set --woa-button-outline-foreground-color
    @Input() @HostBinding("class.override-outline-border-color") overrideOutlineBorderColor: boolean = false;         // Set this and set --woa-button-outline-border-color

    @Input() @HostBinding("class.override-raised-background-color") overrideRaisedBackgroundColor: boolean = false; // Set this and set --woa-button-raised-background-color
    @Input() @HostBinding("class.override-raised-foreground-color") overrideRaisedForegroundColor: boolean = false; // Set this and set --woa-button-raised-foreground-color

    /**
     * Button contents and state.
     */
    @Input() label: string;
    @Input() translationParams: any;
    @Input() icon: string;
    @Input() showSpinner: boolean = false;
    @Input() layout: ButtonLayout = ButtonLayout.HORIZONTAL;
    @Input() @HostBinding("class.disabled") disabled: boolean = false;
    @Input() @HostBinding("class.invisible") invisible: boolean = false;
    @Input() @HostBinding("class.hovered") hovered: boolean = false;

    constructor(private element: ElementRef,
                private sessionModel: SessionModel,
                private changeDetectorRef: ChangeDetectorRef,
                public responsiveService: ResponsiveService) {
        this.determineShapeAndStyle();
    }

    @Input()
    get tier(): ButtonTier {
        return this._tier;
    }

    set tier(tier: ButtonTier) {
        this._tier = tier == undefined ? ButtonTier.FIRST : tier;
        this.determineShapeAndStyle();
    }

    @Input()
    get prominence(): ButtonProminence {
        return this._prominence;
    }

    set prominence(prominence: ButtonProminence) {
        this._prominence = prominence == undefined ? ButtonProminence.HIGH : prominence;
        this.determineShapeAndStyle();
    }

    @Input()
    get connotation(): ButtonConnotation {
        return this._connotation;
    }

    set connotation(connotation: ButtonConnotation) {
        this._connotation = connotation == undefined ? ButtonConnotation.AFFIRMATIVE : connotation;
    }

    @Input()
    get shape(): ButtonShape {
        return this._shape;
    }

    set shape(shape: ButtonShape) {
        this._shape = shape;
        this.shapeSpecified = !!shape;
        this.determineShapeAndStyle();
    }

    @Input()
    get style(): ButtonStyle {
        return this._style;
    }

    set style(style: ButtonStyle) {
        this._style = style;
        this.styleSpecified = !!style;
        this.determineShapeAndStyle();
    }

    @HostBinding("class.native") get native(): boolean {
        return this.responsiveService.isApp;
    }

    @HostBinding("class.woa-button") button = true;

    // Tier classes
    @HostBinding("class.tier-first") get firstTier(): boolean {
        return this._tier === ButtonTier.FIRST;
    }

    @HostBinding("class.tier-second") get secondTier(): boolean {
        return this._tier === ButtonTier.SECOND;
    }

    // Prominence Classes
    @HostBinding("class.prominence-high") get highProminence(): boolean {
        return this._prominence === ButtonProminence.HIGH;
    }

    @HostBinding("class.prominence-low") get lowProminence(): boolean {
        return this._prominence === ButtonProminence.LOW;
    }

    // Connotation classes
    @HostBinding("class.connotation-affirmative") get affirmativeConnotation(): boolean {
        return this._connotation === ButtonConnotation.AFFIRMATIVE;
    }

    @HostBinding("class.connotation-dissuasive") get dissuasiveConnotation(): boolean {
        return this._connotation === ButtonConnotation.DISSUASIVE;
    }

    @HostBinding("class.connotation-negative") get negativeConnotation(): boolean {
        return this._connotation === ButtonConnotation.NEGATIVE;
    }

    // Shape classes
    @HostBinding("class.shape-square") get squareShape(): boolean {
        return this._shape === ButtonShape.SQUARE;
    }

    @HostBinding("class.shape-rounded") get roundedShape(): boolean {
        return this._shape === ButtonShape.ROUNDED;
    }

    @HostBinding("class.shape-oval") get ovalShape(): boolean {
        return this._shape === ButtonShape.OVAL;
    }

    // Style classes
    @HostBinding("class.style-flat") get flatStyle(): boolean {
        return this._style === ButtonStyle.FLAT;
    }

    @HostBinding("class.style-outline") get outlineStyle(): boolean {
        return this._style === ButtonStyle.OUTLINE;
    }

    @HostBinding("class.style-raised") get raisedStyle(): boolean {
        return this._style === ButtonStyle.RAISED;
    }

    // Layout classes
    @HostBinding("class.layout-vertical") get verticalLayout(): boolean {
        return this.layout === ButtonLayout.VERTICAL;
    }

    @HostBinding("class.layout-horizontal") get horizontalLayout(): boolean {
        return this.layout === ButtonLayout.HORIZONTAL;
    }

    @HostListener("click", ["$event"])
    buttonClicked(event: MouseEvent): void {
        if (this.disabled) {
            event.stopPropagation();
        }
    }

    ngOnInit(): void {
        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => {
            this.determineShapeAndStyle();
            this.changeDetectorRef.detectChanges();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * Determine the button shape from the setting in-app config for the tier of this button.
     * Note that default button size properties are configured per-tier in default-theme.scss,
     * and are therefore also configurable via the app config.
     *
     * Determine the button style from the setting in-app config for the prominence/tier combination of this button.
     */
    private determineShapeAndStyle(): void {
        if (this.sessionModel.currentApp) {
            if (!this.shapeSpecified) {
                this._shape = this._tier === ButtonTier.FIRST || !this.sessionModel.currentApp.secondTierButtonShape
                    ? this.sessionModel.currentApp.firstTierButtonShape
                    : this.sessionModel.currentApp.secondTierButtonShape;
            }
            if (!this.styleSpecified) {
                this._style = this._prominence === ButtonProminence.LOW
                    ? (this._tier === ButtonTier.FIRST ? this.sessionModel.currentApp.firstTierLowProminenceButtonStyle : this.sessionModel.currentApp.secondTierLowProminenceButtonStyle)
                    : (this._tier === ButtonTier.FIRST ? this.sessionModel.currentApp.firstTierHighProminenceButtonStyle : this.sessionModel.currentApp.secondTierHighProminenceButtonStyle);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/buttons/button/button.component.ts